import { createContext, useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import ObjectInputService from "../services/ObjectInput";
import { formatBytes, scrollToTop } from "../utils";

const ObjectInputContext = createContext();

// all the request and response handling of API is done here
// all the state management is done here
// it receives four props
// children: the children components
// inputId: the id of the object
// onErrorCb: the callback function to handle the error
// onSaveCb: the callback function to handle the save

const initialHighlightData = () => ({
  objectId: null,
  dependenciesId: [],
  uniqueId: null,
});

export const ObjectInputProvider = ({
  children,
  inputId,
  onErrorCb,
  onSaveCb,
}) => {
  // state

  // this state stores the response from the api
  const [reqResponse, setReqResponse] = useState({
    data: null, // the response from the api
    formattedData: null, // the formatted data against the selected id
    completeTree: null, // the complete tree of the response
    parents: null, // the parents of the selected id
    idValue: null, // the id value pair of the response
  });

  const [showErrorToast, setShowErrorToast] = useState(false);
  // this state is used to keep track of the node that is selcted
  const [id, setId] = useState(inputId);
  // this state keep tracks of the input fields that are visible in tree view like show header
  const [showFields, setShowFields] = useState({});
  // setting this state to true highlights the fields that are in error
  // In order to help the user resolve their error, there is the option to highlight by clicking highlight button on the right of the error in the log
  const [highlight, setHighlight] = useState(false);
  // this field tells if the data is fetched or not, so we could show the loading accordingly
  const [dataFetched, setDataFetched] = useState(false);
  // on error we append the error to the error log
  const [errorLog, setErrorLog] = useState([]);
  // this state is used to show or hide the error logs
  const [showErrors, setShowErrors] = useState(false);
  // settings this state to true, shows all the error in detail
  const [detailedErrorLog, setDetailErrorLog] = useState([]);
  // this state is used to show the saving state of the form, we set it to true when the form is saving
  const [saving, setSaving] = useState(false);
  // whenever we input the value in the input field, we store the value in this state against its node id
  const [idValuePair, setIdValuePair] = useState({});
  // at the top of page we have a button to show the complete tree, this state is used to show or hide the complete tree
  const [showCompleteTree, setShowCompleteTree] = useState(false);
  // we are showing switch view against multiple nodes, this state keeps track of the nodes that are in tree view
  const [treeViewIds, setTreeViewIds] = useState({});
  // inside the tree view, user can hide or show the node by clicking on the tile, this state keeps track of that
  const [visibleHeaderIds, setVisibleHeaderIds] = useState({});
  // this state is used to keep track of the children that are in edit mode
  const [editingChildrenIds, setEditingChildrenIds] = useState({});
  // this state is used to keep track of the node that is in highlight mode
  const [highlightData, setHightLightData] = useState(initialHighlightData());
  // this state is used to keep track of uploading progress
  // const [uploading, setUploading] = useState({
  //   isFile: false,
  //   uploadProgress: 0,
  //   uploading: false,
  //   isSaved: false,
  //   verifying: false,
  // });
  const [uploading, setUploading] = useState({
  });

  // ref
  // this ref prevents the extra rendeing of the useffect on initial reload
  const firstRunRef = useRef(false);
  // this ref will store default saved values so we can restore the default values when someone cancels the editing
  const defaultSavedValues = useRef({});

  const navigates = useNavigate();

  // functions

  const setUploadingData = (id, data, appendData = true) => {
    setUploading({ ...uploading, [id]: appendData ? {...uploading[id], ...data} : {...data}  });
  }

  // inside the tree view, user can hide or show the node by clicking on the tile, this state keeps track of that
  const changeVisibleHeaderValues = (id, value) => {
    if (value === undefined) {
      // toggle state
      value = !visibleHeaderIds[id];
    }
    setVisibleHeaderIds({ ...visibleHeaderIds, [id]: value });
  };

  const toggleShowFields = (id) => {
    setShowFields((prevState) => {
      return { ...prevState, [id]: !prevState[id] };
    });
  };

  const setShowFieldsToTrue = (id) => {
    setShowFields((prevState) => {
      return { ...prevState, [id]: true };
    });
  };

  const changeEditingChildrenIds = (key, value) => {
    setEditingChildrenIds({ ...editingChildrenIds, [key]: value });
  };

  // parsing rule to do the correct error validation
  const parseRuleName = (input) => {
    if (input.includes("GreaterThan")) {
      const res = input.split("GreaterThan");
      return `${res[0]} > ${res[1]}`;
    } else if (input.includes("LessThan")) {
      const res = input.split("LessThan");
      return `${res[0]} < ${res[1]}`;
    } else return input;
  };

  const changeShowErrorToast = (value) => {
    setShowErrorToast(value);
  };

  // this function checks if the  passed id is the id of a sected node
  const isActiveObject = (nodeId) => {
    return nodeId === id;
  };

  // it finds the object by id
  const findObjectById = (id) => {
    return reqResponse.data.find((obj) => obj.id === id);
  };

  // it finds the error object by id
  const findErrorObjectById = (id) => {
    const data = detailedErrorLog.find((obj) => obj.uniqueId === id);
    return (
      data || {
        name: "",
      }
    );
  };

  const handleSetShowErrors = (value = !showErrors) => {
    setShowErrors(value);
  };

  // reset the highlight data
  const emptyHighlightData = () => {
    setHightLightData(initialHighlightData());
  };

  const handleHighlightData = (e, callback) => {
    const errorById = findErrorObjectById(Number(e.target.id));
    const dataToSet = {
      objectId: errorById.errorAtObjectId,
      dependenciesId: errorById.dependencies.map(({ id }) => {
        return id;
      }),
      uniqueId: errorById.uniqueId,
    };

    setHightLightData({
      ...highlightData,
      ...dataToSet,
    });
    if (callback) {
      callback(e, dataToSet);
    }
  };

  const changeMultipleEditingChildrenIdsList = (ids) => {
    let data = { ...editingChildrenIds };
    for (let i = 0; i < ids.length; i++) {
      data[ids[i]] = false;
    }
    setEditingChildrenIds(data);
  };

  // returns true if atleast one children is in edit mode
  const oneChildrenIsInEditMode = (childrenIds) => {
    // Loop over each string in the array
    for (let i = 0; i < childrenIds.length; i++) {
      if (editingChildrenIds[childrenIds[i]]) return true;
    }
    return false;
  };

  const initializeEditingChildrenIds = useCallback((children) => {
    let editData = {};
    for (const child of children) {
      editData[child.id] = child.hasOwnProperty("savedValue")
        ? !(child.savedValue !== null && child.savedValue !== "")
        : false;
    }
    setEditingChildrenIds({ ...editingChildrenIds, ...editData });
    // eslint-disable-next-line
  }, []);

  // handle objectValidation error type inside this function
  const handleObjectValidation = (response) => {
    let apiNo = Math.random() * 16;

    response.error.info.rules.forEach((rule, index) => {
      const newErrorObject = {
        object: findObjectById(rule.errorAtObjectId)?.name || "",
        rule: parseRuleName(rule.name),
        hint: rule.hint,
        ref:
          response.error.type === "internal_server_error" ? response.error.ref : "",
        apiNo,
      };
      if (showErrorToast) {
        setErrorLog((prev) => (prev = [...prev, newErrorObject]));
      } else {
        setErrorLog([newErrorObject]);
      }

      const updatedArray = rule.dependencies.map((item) => {
        const foundObject = findObjectById(item.id);
        if (foundObject) {
          return { id: item.id, name: foundObject.name };
        } else {
          // Handle the case where no matching object is found
          return item;
        }
      });

      const newObject = {
        id: rule.id,
        dependencies: updatedArray,
        object: findObjectById(rule.errorAtObjectId)?.name || "",
        rule: parseRuleName(rule.name),
        hint: rule.hint,
        time: new Date().toISOString(),
        name: rule.name,
        failCondition: response.error.type === "objectValidation"
          ? "rule unsatisfied"
          : "",
        errorAtObjectId: rule.errorAtObjectId,
        unableToUpdateObjectIds: response.error.info.unableToUpdateObjectIds[0],
        type: response.error.type,
        uniqueId: Math.random() * 16,
        ref:
          response.error.type === "internal_server_error" ? response.error.ref : "",
        apiNo,
      };

      setDetailErrorLog((prev) => (prev = [...prev, newObject]));
    });
  };

  // error types other than objectValidation gets handled inside this function
  const handleOtherErrors = (response) => {
    let apiNo = Math.random() * 16;

    const hint = response.error.desc;
    const newErrorObject = {
      object: "",
      rule: "",
      hint: hint,
      ref: response.error.ref,
      apiNo,
    };
    if (showErrorToast) {
      setErrorLog([...errorLog, newErrorObject]);
    } else {
      setErrorLog([newErrorObject]);
    }

    const newObject = {
      hint: hint,
      time: new Date().toISOString(),
      type: response.error.type,
      failCondition: "error from server",
      uniqueId: Math.random() * 16,
      ref: response.error.ref,
      apiNo,
    };

    setDetailErrorLog([...detailedErrorLog, newObject]);
  };

  // this function is called when the input value of the node changes
  const onInputValueChange = (key, value) => {
    setIdValuePair({
      ...idValuePair,
      [key]: value,
    });
  };

  const getUpdatedValues = (updatedObjects) => {
    let updatedObjectsValue = {};
    let updatedObjectsIds = [];
    for (let i = 0; i < updatedObjects?.length; i++) {
      updatedObjectsValue[updatedObjects[i].id] = updatedObjects[i].value;
      updatedObjectsIds.push(updatedObjects[i].id);
    }
    return { updatedObjectsValue, updatedObjectsIds };
  };

  // saving the form data on the api side
  const saveData = async (value, type, onSave, onError) => {
    let response;
    try {
      setSaving(true);
      if (type === "File") {
        response = await ObjectInputService.saveFileObject(
          value.triggerObject.id
        );
        setUploadingData(value.triggerObject.id, {
          isFile: false,
          uploadProgress: 0,
          isSaved: !response.error,
          verifying: false,
        });

        if(response.result) {
          const updatedObjectsValue = {[value.triggerObject.id]: response.result}
          setIdValuePair({
            ...idValuePair,
            ...updatedObjectsValue,
          });
          defaultSavedValues.current = {
            ...defaultSavedValues.current,
            ...updatedObjectsValue,
          };
          setEditingChildrenIds({ ...editingChildrenIds, [value.triggerObject.id]: false });
        }

      } else {
        response = await ObjectInputService.updateObjectInput(value);
      }

      if (response.result?.updatedObjects) {
        const { updatedObjectsValue, updatedObjectsIds } = getUpdatedValues(
          response.result.updatedObjects
        );
        setIdValuePair({
          ...idValuePair,
          ...updatedObjectsValue,
        });
        defaultSavedValues.current = {
          ...defaultSavedValues.current,
          ...updatedObjectsValue,
        };
        changeMultipleEditingChildrenIdsList(updatedObjectsIds);
      }

      if (response.error) {
        if (response.error.type === "objectValidation") {
          handleObjectValidation(response);
        } else {
          handleOtherErrors(response);
        }

        if (!showErrors) {
          changeShowErrorToast(true);
        }
        scrollToTop();
        if (onErrorCb) onErrorCb(response.error);
        if (onError) onError();
        return;
      }
      if (onSaveCb) onSaveCb(value);
      if (onSave) onSave(value);
    } catch (e) {
      if (onError) onError();
      if (onErrorCb) onErrorCb(e);
    } finally {
      setSaving(false);
    }
  };

  // recursively searches a nested object (tree) for an object with a specific id, returning that object and its parents.
  const findMatchedNestedObject = (obj, id, parents = []) => {
    for (const key in obj) {
      if (key === id) {
        return {
          parents: parents,
          object: obj[key],
        };
      }
      if (obj[key].children) {
        const result = findMatchedNestedObject(obj[key].children, id, [
          ...parents,
          { id: obj[key].id, name: obj[key].name },
        ]);
        if (result) {
          return result;
        }
      }
    }
    return null;
  };

  // recursive function to associate the children with parent
  // every parent needs to know the list of its children so it always remain in sync with its children
  const associatedObjectsTree = (data, result, titleList, level = 1) => {
    let { children, ...formattedData } = data;
    formattedData.children = children;

    if (formattedData.hasOwnProperty("savedValue"))
      formattedData.value = formattedData.savedValue;

    if (children) {
      let associatedObjects = [];
      if (level > 1) {
        associatedObjects = [
          {
            attributeOfObjectId: formattedData.attributeOfObjectId,
            hide: true,
            level,
            ...formattedData,
          },
        ];
      }

      Object.values(children).map((item, index) => {
        associatedObjects = [
          ...associatedObjects,
          ...associatedObjectsTree(
            item,
            result,
            level === 1 ? [] : [...titleList, { id: data.id, name: data.name }],
            level + 1
          ),
        ];
        return associatedObjects;
      });
      if (level === 1) {
        formattedData.associatedObjects = associatedObjects;
        formattedData.level = level;
      } else return associatedObjects;
    }

    formattedData.titleList = [
      ...titleList,
      { id: formattedData.id, name: formattedData.name },
    ];
    formattedData.level = level;

    return [formattedData];
  };

  // converting the api response into the tree like structure
  // once the tree like structure is created, we are calling the associatedObjectsTree
  // to format the tree so that we can pass the data in the api request
  // build tree function returns the data with following keys
  // completeTree: all the nodes till the root node in hierarchical structure, at the top root node is present
  // data: it is the response from the api, without any formatting
  // formattedData: contains the tree like structure against the selected id. At root level, selected object will be present
  // idValue: contains all the nodes against its id
  // parentDependenciesPair: inside the rules tab of node we need to show the linked parents of that node
  // parents: list of parents of selected node

  // here is how build tree works
  // first it creates a tree structures of response
  // then it creates an id value pair
  // then it associates children with node using associatedObjectsTree function
  // then if the selected id is different from parent id
  // it extracts the selected node from the tree using the getTreeAgainstId function and returns it as formattedData field
  // if the selected node is not found in the tree, it returns the root node as formattedData
  const buildTree = (data) => {
    let tree = {};
    let idValuePairCp = {};
    let idValue = {};
    // Create a map to store parent dependencies pair, which is used to highlight depdendent parents
    // if a node is a dependent on other nodes then we need to keep track of it
    let parentDependenciesPair = {};

    // Create a map to store objects by their ids for quick access
    const objectsById = {};
    data.forEach((obj) => {
      objectsById[obj.id] = obj;
      const attributeRules = obj.rules.attributeRules;
      const classRules = obj.rules.classRules;

      for (let attributeRule of attributeRules) {
        if (attributeRule?.dependencies) {
          for (let dependency of attributeRule?.dependencies) {
            if (!parentDependenciesPair[dependency.id]) {
              parentDependenciesPair[dependency.id] = [];
            }
            parentDependenciesPair[dependency.id].push({
              type: "attr",
              id: obj.id,
            });
          }
        }
      }

      for (let classRule of classRules) {
        if (classRule?.dependencies) {
          for (let dependency of classRule?.dependencies) {
            if (!parentDependenciesPair[dependency.id]) {
              parentDependenciesPair[dependency.id] = [];
            }
            parentDependenciesPair[dependency.id].push({
              type: "class",
              id: obj.id,
            });
          }
        }
      }

      if (obj.hasOwnProperty("savedValue")) {
        if(obj.class === "File" && !obj.savedValue && obj.rtOptions?.tmpSavedValue) {
          obj.savedValue = obj.rtOptions.tmpSavedValue;
        }
        idValuePairCp[obj.id] = obj.savedValue;
        defaultSavedValues.current[obj.id] = obj.savedValue;
      }

      obj.childrenIds = [];
    });

    setIdValuePair(idValuePairCp);

    // Iterate through each object and build the tree
    data.forEach((obj) => {
      const parentId = obj.attributeOfObjectId;

      idValue[obj.id] = { ...obj };

      if (parentId === null || !objectsById[parentId]) {
        // This node is either a root node or has an unmatched parentId
        tree[obj.id] = obj;
      } else {
        if (!objectsById[parentId].children) {
          objectsById[parentId].children = {};
        }
        objectsById[parentId].children[obj.id] = obj;
        objectsById[parentId].childrenIds.push(obj.id); // Add child ID to parent's childrenIds
        objectsById[parentId].childrenIds = [
          ...new Set(objectsById[parentId].childrenIds),
        ]; // Remove duplicates

        // Propagate childrenIds to parent's ancestors
        let ancestor = objectsById[parentId];
        let attributeOfObjectId = ancestor?.attributeOfObjectId;
        if (attributeOfObjectId) {
          ancestor.childrenIds.push(obj.id);
          ancestor.childrenIds = [...new Set(ancestor.childrenIds)]; // Remove duplicates
          while (attributeOfObjectId) {
            ancestor = objectsById[ancestor.attributeOfObjectId];
            ancestor.childrenIds.push(obj.id);
            ancestor.childrenIds = [...new Set(ancestor.childrenIds)];
            attributeOfObjectId = ancestor?.attributeOfObjectId;
          }
        }
      }
    });

    const completeTree = tree;
    return {
      completeTree,
      idValue,
      parentDependenciesPair,
      ...getTreeAgainstId(tree, completeTree),
    };
  };

  // retrieves a subtree from a given tree based on a specific id, and also collects associated objects and parent information for that subtree.
  const getTreeAgainstId = (tree, completeTree) => {
    let parents = [];

    // id of the selected object
    tree = tree[id] ? { ...tree[id] } : null;

    // if the root object id is different from the selected object id, we need to find the selected object in the tree
    if (!tree) {
      // get the selected object and its parents
      const matchObject = findMatchedNestedObject(completeTree, id, parents);
      // if the selected object is found, assign selected object to tree
      tree = matchObject.object;
      parents = matchObject.parents;
    }

    tree.titleList = [{ id: tree.id, name: tree.name }];

    let { children, ...result } = tree;
    result.associatedObjects = [];
    result.children = children;
    if (children)
      Object.values(children).map((item) => {
        return (result.associatedObjects = [
          ...result.associatedObjects,
          ...associatedObjectsTree(item, result, [], 1),
        ]);
      });
    else {
      result.titleList = [{ id: result.id, name: result.name }];
      if (defaultSavedValues.current.hasOwnProperty(result.id)) {
        result.value = defaultSavedValues.current[result.id];
      } else result.value = result.savedValue;
    }
    return { tree: result, parents };
  };

  // fetches data from ObjectInputService based on an id, initializes editing children ids if necessary, builds a tree from the response, and updates the reqResponse state with the fetched and processed data.
  const sendInitialRequest = async () => {
    const response = await ObjectInputService.getObjectInput(id);
    setDataFetched(true);
    if (response) {
      response.result?.length &&
        !reqResponse.formattedData &&
        initializeEditingChildrenIds(response.result);
      const treeData = response.result?.length
        ? buildTree(response.result)
        : {
            completeTree: null,
            tree: null,
          };
      setReqResponse({
        data: response.result,
        formattedData: treeData.tree,
        completeTree: treeData.completeTree,
        parents: treeData.parents,
        idValue: treeData.idValue,
        parentDependenciesPair: treeData.parentDependenciesPair,
      });
    }
  };

  // creating the request data to send to the api, we need to format the data before sending it to the api
  const createRequestData = (inputData) => {
    let associatedObjectsArray = [];
    let requestData = {
      triggerObject: { id: inputData.id },
    };

    const createFormattedChildren = (children) => {
      const childrenData = reqResponse.idValue[children];
      let formattedChildren = {
        id: childrenData.id,
        attributeOfObjectId: childrenData.attributeOfObjectId,
      };
      if (childrenData.hasOwnProperty("savedValue")) {
        let childrenValue = idValuePair[childrenData.id];
        formattedChildren.value =
          (childrenData.class === "Integer" ||
            childrenData.class === "Float") &&
          childrenValue !== null
            ? Number(childrenValue)
            : childrenValue;
      }
      return formattedChildren;
    };
    inputData.childrenIds.forEach((children) => {
      associatedObjectsArray.push(createFormattedChildren(children));
    });
    requestData.associatedObjects = associatedObjectsArray;
    return requestData;
  };

  const navigatesUsingId = (e) => {
    navigates(`/input/${e.target.id}`);
  };

  const setActiveObjectId = (e) => {
    setId(e.target.id);
  };

  // inputId comes from parent, there is a possibilty that parent may send the  new id after the component is mounted
  useEffect(() => {
    setId(inputId);
  }, [inputId]);

  useEffect(() => {
    if (!firstRunRef.current && id) {
      sendInitialRequest();
      firstRunRef.current = true;
    }

    if (reqResponse.data) {
      const newTree = getTreeAgainstId(
        reqResponse.completeTree,
        reqResponse.completeTree
      );

      setReqResponse({
        ...reqResponse,
        formattedData: newTree.tree,
        parents: newTree.parents,
      });
    }
    // eslint-disable-next-line
  }, [id]);

  return (
    <ObjectInputContext.Provider
      value={{
        reqResponse,
        setReqResponse,
        saveData,
        dataFetched,
        saving,
        setSaving,
        createRequestData,
        changeEditingChildrenIds,
        editingChildrenIds,
        oneChildrenIsInEditMode,
        onInputValueChange,
        idValuePair,
        showCompleteTree,
        setShowCompleteTree,
        uploadingData: uploading,
        setUploading: setUploadingData,
        errorLog,
        setErrorLog,
        detailedErrorLog,
        setDetailErrorLog,
        findObjectById,
        handleHighlightData,
        highlightData,
        findErrorObjectById,
        emptyHighlightData,
        handleSetShowErrors,
        showErrors,
        setShowErrors,
        navigatesUsingId,
        showErrorToast,
        setShowErrorToast,
        changeShowErrorToast,
        showFields,
        toggleShowFields,
        highlight,
        setHighlight,
        changeVisibleHeaderValues,
        visibleHeaderIds,
        setShowFieldsToTrue,
        treeViewIds,
        setTreeViewIds,
        isActiveObject,
        id,
        setId: (value) => setId(value.toString()),
        setActiveObjectId,
        handleOtherErrors,
        formatBytes,
        defaultSavedValues: defaultSavedValues.current,
      }}
    >
      {children}
    </ObjectInputContext.Provider>
  );
};

export default ObjectInputContext;
